import type { Client } from '@accounts/transport';
import type { Role } from './types';

/**
 * What a permission does, as its product's catalog says: it only reads, it changes the person's work, it
 * reaches outside Beyond, or it can cost money. A permission without it stands apart, never chosen for
 * the person.
 */
export type Effect = 'read' | 'change' | 'publish' | 'cost';

/** A scope as the consent and AI apps with access show it: its full name and what it means, by language */
export interface Scope {
	/** `<product>:<name>`, as the application asked for it */
	name: string;
	/** Its words by language; `null` when the product no longer declares it */
	words: Record<string, string> | null;
	/** The capability's short name by language, when the product's catalog gives one */
	title?: Record<string, string>;
	effect?: Effect;
}

/** What an application already has for the resource, for one organization or (`null`) for all of them */
export interface Held {
	connection: string;
	organization: string | null;
	/** What a decision that changes it reviews */
	version: number;
	scopes: Scope[];
}

/** What Allow sends: the permissions selected of the request, those removed from what it had, and the version reviewed */
export interface Selected {
	scopes: string[];
	remove: string[];
	basis: number | null;
}

/**
 * What an application asks of the person, read from a consent ticket (`GET /v1/oauth/consent`).
 *
 * The application's name is its own claim (`verified: false`): Accounts read it from the application's
 * description or registration and has not checked it, which is why the consent says so beside where
 * the answer goes.
 */
export interface Connecting {
	client: {
		id: string;
		name: string;
		uri?: string;
		logo?: string;
		kind: 'metadata' | 'registered';
		verified: false;
		/** Where the answer goes: the redirect's host and port, and whether it is this computer */
		redirect: { host: string; loopback: boolean };
	};
	product: { id: string; name: string };
	resource: string;
	/** What it asks for now */
	scopes: Scope[];
	/** What it already has, one entry per organization it reaches (`null`: all of them) */
	existing: Held[];
	/** The person's active organizations, any one of which the connection may be narrowed to */
	organizations: { organization: string; name: string; role: Role }[];
	person: { name: string | null };
	expires: string;
	/** When what is allowed now ends, unless it is allowed again */
	until: string;
	/** Whether Allow first asks the person to confirm it is them */
	reauthenticate: boolean;
}

/** One live connection, as AI apps with access lists it (`GET /v1/account/connections`) */
export interface Connection {
	id: string;
	client: { id: string; name: string; kind: 'metadata' | 'registered'; redirect?: string | { host: string; loopback?: boolean } };
	product: { id: string; name: string };
	resource: string;
	scopes: Scope[];
	/** The organization it is narrowed to; absent when it follows the person in all of theirs */
	organization?: string;
	/** What a narrowing reviews */
	version: number;
	created: string;
	used?: string;
	expires: string;
}

/**
 * Why the service could not even ask the person (`/connect?problem=<code>`): the application is not
 * known, its description is not valid or could not be read, its redirect is not one it registered, or
 * Accounts could not answer. Anything else the address carries is `other`.
 */
export type Cause = 'client' | 'document' | 'unreachable' | 'redirect' | 'unavailable' | 'other';

/**
 * The applications a person connects to a product (OAuth 2.1, the service's "Connecting an application
 * to a product for a person"): the consent's read and its decision, and the person's AI apps with access.
 *
 * The decision and a disconnection are never sent again after a renewal of the session (`replay:
 * false`): the person decides again, on a page that reads the request afresh.
 */
export class Connections {
	static CAUSES: Cause[] = ['client', 'document', 'unreachable', 'redirect', 'unavailable'];

	#client: Client;

	constructor(client: Client) {
		this.#client = client;
	}

	/**
	 * What an application asks. The ticket is the service's, bound to this session, and reaches nothing
	 * but this read and `decide`.
	 */
	async ask(ticket: string): Promise<Connecting> {
		return this.#client.get<Connecting>(`/v1/oauth/consent?ticket=${encodeURIComponent(ticket)}`);
	}

	/**
	 * Allow with what the person chose, once. Never sent without a selection: the service refuses one
	 * that names none rather than read it as everything the application asked for.
	 *
	 * @param organization The one organization an allowed connection is narrowed to; none for all of the
	 * person's organizations
	 * @returns The address the service answered (the application's redirect with a code), the only one
	 * this interface goes to
	 */
	async allow(ticket: string, selected: Selected, organization?: string): Promise<string> {
		const body = { ticket, allowed: true, ...selected, ...(organization ? { organization } : {}) };
		const decided = await this.#client.post<{ destination: string }>('/v1/oauth/consent', body, { replay: false });
		return decided.destination;
	}

	/** Cancel, once: the application learns the person did not allow it, and keeps what it already had */
	async cancel(ticket: string): Promise<string> {
		const decided = await this.#client.post<{ destination: string }>('/v1/oauth/consent', { ticket, allowed: false }, { replay: false });
		return decided.destination;
	}

	/** The person's live connections, newest first */
	async list(): Promise<Connection[]> {
		return (await this.#client.get<{ connections: Connection[] }>('/v1/account/connections')).connections;
	}

	/** Keeps only part of what a connection may do, on the version the person reviewed */
	async narrow(connection: Connection, scopes: string[]): Promise<Connection> {
		return this.#client.patch<Connection>(`/v1/account/connections/${encodeURIComponent(connection.id)}`, { scopes, version: connection.version }, { replay: false });
	}

	/** Ends a connection and every token of it at once */
	async end(id: string): Promise<void> {
		await this.#client.delete(`/v1/account/connections/${encodeURIComponent(id)}`, { replay: false });
	}

	/** A scope's words in a language, else in English; `undefined` when the product no longer describes it */
	static words(scope: Scope, language: string): string | undefined {
		return scope.words?.[language] ?? scope.words?.en;
	}

	/** A scope's short name in a language, else in English, else its words; `undefined` when it has neither */
	static title(scope: Scope, language: string): string | undefined {
		return scope.title?.[language] ?? scope.title?.en ?? Connections.words(scope, language);
	}

	/** Whether the words shown are not in the person's language */
	static foreign(scope: Scope, language: string): boolean {
		return Boolean(scope.words && !scope.words[language]);
	}

	/** Where a connection's answers go, as a host, whichever shape the service gave */
	static host(connection: Connection): string | undefined {
		const redirect = connection.client.redirect;
		return typeof redirect === 'string' ? redirect : redirect?.host;
	}

	/** The cause an address names, `other` for one this interface does not know */
	static cause(value: string | null): Cause {
		return Connections.CAUSES.find(cause => cause === value) ?? 'other';
	}
}
